import apiGlider from '@/api/glider'
import apiMission from '@/api/mission'
import { buildDataMissionKey, DataPoolMissionKeys } from '@/enum/DataPool'
import { GliderFileImportStep } from '@/enum/GliderFileImportStep'
import { GliderFileType } from '@/enum/GliderFileType'
import { i18n } from '@/locale/vueI18n'
import { AloBaseArray } from '@/models/alo/AloBaseArray'
import { AloError } from '@/models/alo/AloError'
import { AloGliderFileShort } from '@/models/alo/AloGliderFileShort'
import { AloLogbookChannel } from '@/models/alo/AloLogbookChannel'
import { AloLogbookItem } from '@/models/alo/AloLogbookItem'
import { AloMission, GLIDER_MODE } from '@/models/alo/AloMission'
import { AloMissionPlan } from '@/models/alo/AloMissionPlan'
import { AloUploadResult } from '@/models/alo/AloUploadResult'
import { IrisConfigContainer } from '@/models/iris/IrisConfigContainer'
import { IrisrawGliderState } from '@/models/iris/IrisrawGliderState'
import { adaptIrisGliderStateToRuntime } from '@/models/iris/gliderState/gliderRuntimeAdapter'
import { GliderModuleApiId, MODULE_API_ID_TO_WIRE_KEY } from '@/models/iris/gliderState/IGliderRuntime'
import { IrisrawModMissionPlan } from '@/models/iris/IrisrawModMissionPlan'
import { IrisrawModMissionPlanConfig } from '@/models/iris/IrisrawModMissionPlanConfig'
import { IrisrawModSendMail } from '@/models/iris/IrisrawModSendMail'
import { ApiResultCode } from '@/models/response/AlrBase'
import { ConsoleRow } from '@/models/ui/ConsoleRow'
import { buildMissionPlanStateId, MissionPlanUiState } from '@/models/ui/MissionPlanUiState'
import { MissionUi } from '@/models/ui/MissionUi'
import { buildMissionUIStateId } from '@/models/ui/MissionUiState'
import { WobGliderFile } from '@/models/wob/WobGliderFile'
import { IRootState } from '@/store'
import types from '@/store/all-mutation-types'
import util from '@/utils/classToObject'
import Vue from 'vue'
import { ActionContext } from 'vuex'
import { IMissionState } from './state'
//@ts-ignore
import { AloSecurityItem } from '@/models/alo/AloSecurityItem'
import { SecurityItemsKeys } from '@/enum/SecurityItems'
import { SoundNotification } from '@/enum/SoundNotification'
import { AloYo } from '@/models/alo/AloYo'
import { AloDbEnseigne } from '@/models/alodb/AloDbEnseigne'
import { IrisrawModAd2cp } from '@/models/iris/IrisrawModAd2cp'
import { IrisrawModAis } from '@/models/iris/IrisrawModAis'
import { IrisrawModQuickControlConfig } from '@/models/iris/IrisrawModQuickControlConfig'
import { IQuickControlConfig } from '@/models/iris/quickControlConfig/IQuickControlConfig'
import { IrisrawModCoriolis } from '@/models/iris/IrisrawModCoriolis'
import { ISmartMission } from '@/models/iris/smartMission/ISmartMission'
import { ISmartSimulation } from '@/models/iris/smartMission/ISmartSimulation'
import { wobGliderNmea } from '@/models/wob/WobGliderNmea'
import { WobSimProgress } from '@/models/wob/WobSimProgress'
import deepEqual from 'deep-equal'

/**
 * Helper — extrait le flag enable d'un IrisConfigContainer<T> et dispatch un
 * patch sur gliderRuntime.modules. No-op si le config est null/incomplet ou si
 * le gliId n'est pas résoluble. Centralise la traduction wire→domain (cf
 * MODULE_API_ID_TO_WIRE_KEY).
 */
function patchRuntimeModuleEnable(
  context: ActionContext<any, any>,
  moduleKey: GliderModuleApiId,
  irisConfig: unknown,
  gliId: number | null | undefined,
) {
  if (!irisConfig || gliId == null) return
  const wireKey = MODULE_API_ID_TO_WIRE_KEY[moduleKey]
  const enabledMod = (irisConfig as { mod_settings?: { EnabledMOD?: Record<string, unknown> } })?.mod_settings
    ?.EnabledMOD
  const enable = enabledMod?.[wireKey]
  if (typeof enable === 'boolean') {
    context.dispatch(types.GLIDER_RUNTIME_PATCH_MODULE_ENABLE, { gliId, moduleKey, enable })
  }
}

async function applyAisSecurityOverride(missions: AloMission[], context: ActionContext<any, any>) {
  // Gate Anticollision par l'enseigne du glider de la mission (mission.ensId),
  // pas par l'enseigne de l'utilisateur.
  //
  // Algorithme :
  //  - mission.ensId === user.ensId → utiliser userProfile.config (toujours
  //    dispo, chargé depuis le JWT à l'auth — aucun fetch nécessaire).
  //  - sinon → utiliser enseignesById[mission.ensId]?.config (nécessite que
  //    la liste enseignes soit chargée).
  //
  // Pour les rôles multi-enseignes (sysadmin / operator — ceux qui ont le
  // sélecteur d'enseigne dans le header, cf MissionOperatorFilter.vue:2), on
  // AWAIT ENSEIGNE_ENSURE_LIST avant le gate pour garantir le lookup per-mission.
  // Pour les autres rôles, on saute le fetch — ils ne sont censés voir que
  // les missions de leur propre enseigne, donc le path userProfile.config
  // couvre tous les cas. Si malgré tout une mission « étrangère » apparaît,
  // le lookup enseignesById tombera sur undefined → deny-by-default.
  const userProfile = context.rootState.auth.userProfile
  const isMultiMission = context.rootGetters.isSysAdmin || context.rootGetters.isOperator
  if (isMultiMission) {
    await context.dispatch(types.ENSEIGNE_ENSURE_LIST).catch(() => {
      // 403 / réseau / etc. — on continue en deny-by-default pour les missions
      // d'enseignes étrangères ; les missions de l'enseigne du user restent
      // évaluées via userProfile.config.
    })
  }
  const enseignesById = context.rootGetters.enseignesById ?? {}
  missions.forEach(m => {
    const config =
      m.ensId != null && m.ensId === userProfile?.ensId
        ? userProfile?.config
        : m.ensId != null
        ? (enseignesById[m.ensId] as AloDbEnseigne | undefined)?.config
        : undefined
    if (!config?.ais_on || !config?.ais_anticollision_on) {
      m.security[SecurityItemsKeys.PILOT_DETAIL_ANTICOLLISION] = new AloSecurityItem({ enable: false, visible: false })
    }
  })
}

export class MissionAction {
  public [types.MISSION_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { ongoing, yos, gliIds }: { ongoing?: boolean | null; yos?: boolean; gliIds?: Array<number> | null },
  ) {
    if (ongoing == undefined) ongoing = null
    if (yos == undefined) yos = true
    if (gliIds == undefined) gliIds = null
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_REQUEST, ongoing)
      apiMission
        //@ts-ignore
        .getMissions(ongoing, yos, gliIds)
        .then((resp: any) => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const missions = resp.data.data as AloBaseArray<AloMission>
            context
              .dispatch(types.UI_ENSURE_MISSION_STATE, { ids: missions.items.map(m => m.misId), typeUiid: 'global' })
              .then(async () => {
                //context.commit les données de mission
                if (ongoing == null || ongoing) {
                  await applyAisSecurityOverride(missions.items, context)
                  context.commit(types.MISSION_SUCCESS, missions.items)
                } else if (!yos) {
                  context.commit(types.MISSION_PASSED_PRELOAD_SUCCESS, missions.items)
                } else {
                  context.commit(types.MISSION_PASSED_SUCCESS, { items: missions.items, gliIds })
                }
                resolve(resp)
              })
          } else {
            context.commit(types.MISSION_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch((resp: any) => {
          context.commit(types.MISSION_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_LOADING_REQUEST](context: ActionContext<IMissionState, IRootState>, id: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_LOADING_REQUEST, id)
      apiMission
        .getMission(id)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const mission = resp.data.data as AloMission
            //lance la creation des models de mission state Ui si necessaire
            context
              .dispatch(types.UI_ENSURE_MISSION_STATE, { ids: [mission.misId], typeUiid: 'global' })
              .then(async () => {
                //context.commit les données de la mission
                await applyAisSecurityOverride([mission], context)
                context.commit(types.MISSION_LOADING_SUCCESS, mission)
                //Lance la recuperation du state du glider correspondant à la mission
                if (mission.misActifOn) context.dispatch(types.MISSION_IRIS_GLIDER_STATE_REQUEST, mission.misId)
                resolve(mission)
              })
          } else {
            context.commit(types.MISSION_LOADING_ERROR, id)
            reject()
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_LOADING_ERROR, id)
          reject()
        })
    })
  }
  public [types.MISSION_LOADING_SCIVARIABLE_REQUEST](context: ActionContext<IMissionState, IRootState>, id: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_LOADING_SCIVARIABLE_REQUEST, id)
      apiMission
        .getMission(id)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const mission = resp.data.data as AloMission
            context.commit(types.MISSION_LOADING_SCIVARIABLE_SUCCESS, mission)
            resolve(mission)
          } else {
            context.commit(types.MISSION_LOADING_SCIVARIABLE_ERROR, id)
            reject()
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_LOADING_SCIVARIABLE_ERROR, id)
          reject()
        })
    })
  }
  public [types.MISSION_DATA_SCIENCE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { id, targetYosCycles }: { id: number; targetYosCycles: Array<number> | null },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_DATA_SCIENCE_REQUEST, id)
      apiMission
        .getDataScience(id, targetYosCycles)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const datas = resp.data.data
            //context.commit les données de la mission
            context.commit(types.MISSION_DATA_SCIENCE_SUCCESS, { id, datas, targetYosCycles })
            resolve(datas)
          } else {
            context.commit(types.MISSION_DATA_SCIENCE_ERROR, id)
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_DATA_SCIENCE_ERROR, id)
          reject()
        })
    })
  }

  public [types.MISSION_DRIFT_REQUEST](context: ActionContext<IMissionState, IRootState>, id: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_DRIFT_REQUEST, id)
      apiMission
        .getDrift(id)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const yos = resp.data.data as AloBaseArray<AloYo>
            //lance la creation des models de mission state Ui si necessaire
            context.dispatch(types.UI_ENSURE_MISSION_STATE, { ids: [id], typeUiid: 'global' }).then(() => {
              //context.commit les données de la mission
              context.commit(types.MISSION_DRIFT_SUCCESS, { id, yos })
              resolve(yos)
            })
          } else {
            context.commit(types.MISSION_DRIFT_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_DRIFT_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_GLIDER_STATE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    missionId: number,
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_GLIDER_STATE_REQUEST)
      const mission = context.getters.missionsById[missionId]
      if (mission !== undefined) {
        apiGlider
          .getIrisGliderState(mission.gliId)
          .then(resp => {
            if (resp.data.resultCode == ApiResultCode.Ok) {
              const gliderStateConfig = resp.data.data as IrisrawGliderState
              const gliderRuntime = adaptIrisGliderStateToRuntime(gliderStateConfig.data, mission.gliId)
              context.commit(types.MISSION_IRIS_GLIDER_STATE_SUCCESS, {
                id: missionId,
                gliderRuntime,
              })
              resolve(gliderStateConfig.data)
            } else {
              context.commit(types.MISSION_IRIS_GLIDER_STATE_ERROR, missionId)
              const error = resp.data.data as AloError
              console.error(error.errorMessage)
              reject(error)
            }
          })
          .catch(resp => {
            context.commit(types.MISSION_IRIS_GLIDER_STATE_ERROR, missionId)
            reject()
          })
      } else {
        reject()
      }
    })
  }

  public [types.MISSION_DATA_NAVIGATION_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { id, targetYosCycles }: { id: number; targetYosCycles: Array<number> | null },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_DATA_NAVIGATION_REQUEST, id)
      apiMission
        .getDataNavigation(id, targetYosCycles)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const datas = resp.data.data
            //context.commit les données de la mission
            context.commit(types.MISSION_DATA_NAVIGATION_SUCCESS, { id, datas, targetYosCycles })
            resolve(datas)
          } else {
            context.commit(types.MISSION_DATA_NAVIGATION_ERROR, id)
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_DATA_NAVIGATION_ERROR, id)
          reject()
        })
    })
  }

  public [types.MISSION_DATA_NMEA_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { typeNmea, id }: { typeNmea: string; id: number },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_DATA_NMEA_REQUEST, id)
      apiMission
        .getDataNmea(typeNmea, id)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const data = resp.data.data
            //context.commit les données de la mission
            context.commit(types.MISSION_DATA_NMEA_SUCCESS, { id, typeNmea, data })
            resolve(data)
          } else {
            context.commit(types.MISSION_DATA_NMEA_ERROR, { id, typeNmea })
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_DATA_NMEA_ERROR, { id, typeNmea })
          reject()
        })
    })
  }

  public [types.MISSION_UNSELECT](context: ActionContext<IMissionState, IRootState>) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_UNSELECT)
      resolve()
    })
  }

  public [types.MISSION_SELECT_DETAIL_REQUEST](context: ActionContext<IMissionState, IRootState>, id: number) {
    return new Promise((resolve, reject) => {
      context
        .dispatch(types.MISSION_ENSURE_MISSION, id)
        .then(mission => {
          Promise.all([
            context.dispatch(types.UI_ENSURE_MISSION_STATE, { ids: [mission.misId], typeUiid: 'detail' }),
            context.dispatch(types.UI_ENSURE_MISSION_STATE, { ids: [-mission.misId], typeUiid: 'detail' }),
          ]).then(() => {
            context.commit(types.MISSION_SELECT_DETAIL_SUCCESS, mission)
            context.dispatch(types.MISSION_SELECT_DETAIL, mission.misId)
            //TODO VIRER une fois la variable dans le model
            /*if(mission.misActifOn) {              
              context.dispatch(types.GLIDER_IRIS_EXPECTED_NMEA_REQUEST,mission.gliId)
            }*/
            resolve(mission)
          })
        })
        .catch(() => {
          context.commit(types.MISSION_SELECT_DETAIL_ERROR, id)
          reject()
        })
    })
  }

  public [types.MISSION_UNSELECT_DETAIL](context: ActionContext<IMissionState, IRootState>, id: number) {
    context.commit(types.MISSION_UNSELECT_DETAIL, id)
    return context.dispatch(types.UI_RESET_UISTATE, buildMissionUIStateId(id, 'detail'))
  }

  public [types.MISSION_SELECT_DETAIL](context: ActionContext<IMissionState, IRootState>, id: number) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_SELECT_DETAIL, id)
      resolve()
    })
  }

  public [types.MISSION_NAVTO_DETAIL](
    context: ActionContext<IMissionState, IRootState>,
    { cmp, isleft, id, label }: { cmp: string; isleft: boolean; id: number; label: string },
  ) {
    context.dispatch(types.UI_SET_MEASURETOOL, 'NONE')
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_NAVTO_DETAIL, { cmp, isleft, id, label })
      resolve()
    })
  }

  public [types.MISSION_SET_LEFTPANEL_SIZE](context: ActionContext<IMissionState, IRootState>, size: string) {
    context.commit(types.MISSION_SET_LEFTPANEL_SIZE, size)
  }

  public [types.MISSION_UNSELECT_TAB](
    context: ActionContext<IMissionState, IRootState>,
    { cmp, isleft, id }: { cmp: string; isleft: boolean; id: number },
  ) {
    const tabsMission = isleft
      ? context.state.currentDetailMission.tabsleft
      : context.state.currentDetailMission.tabsright
    const tab = tabsMission.tabs.find(it => it.cmp == cmp && it.id == id)
    if (tab && tab.checkBeforeClose) {
      context.commit(types.MISSION_UNSELECT_TAB, tab)
    } else {
      context.dispatch(types.MISSION_CLOSE_TAB, { cmp, isleft, id })
    }
  }

  public [types.MISSION_CLOSE_TAB](
    context: ActionContext<IMissionState, IRootState>,
    { cmp, isleft, id }: { cmp: string; isleft: boolean; id: number },
  ) {
    //TODO Recup l'instance du component
    //context.dispatch(types.MISSION_CLOSE_TAB, { cmp, isleft, id })
    context.commit(types.MISSION_CLOSE_TAB, { cmp, isleft, id })
    context.dispatch(types.UI_RESET_UISTATE, `${cmp}-${id}`)
  }

  public [types.MISSION_RENAME_DETAIL](
    context: ActionContext<IMissionState, IRootState>,
    { cmp, isleft, id, label }: { cmp: string; isleft: boolean; id: number; label: string },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_RENAME_DETAIL, { cmp, isleft, id, label })
      resolve(label)
    })
  }

  public [types.MISSION_MOVE_TAB](
    context: ActionContext<IMissionState, IRootState>,
    { cmp, isleft, id }: { cmp: string; isleft: boolean; id: number },
  ) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_MOVE_TAB, { cmp, isleft, id })
      resolve()
    })
  }

  public [types.MISSION_SAVE_IMAGE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { imageData, name }: { imageData: any; name: string },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_SAVE_IMAGE_REQUEST, name)
      const saveFunc = name.startsWith('home') ? apiMission.savePublicImage : apiMission.saveImageMission
      saveFunc({ imageData, name })
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const uploadResult = resp.data.data as AloUploadResult
            context.commit(types.MISSION_SAVE_IMAGE_SUCCESS, name)
            resolve(uploadResult)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_SAVE_IMAGE_ERROR, name)
          reject()
        })
    })
  }

  public [types.MISSION_SET_GLIDER_FILTER](context: ActionContext<IMissionState, IRootState>, ids: number[]) {
    return new Promise((resolve, reject) => {
      //Regarde les ids à requete
      const queryIds = ids.filter(id => context.state.missionsPassedLoaded.every(pid => pid != id))
      if (queryIds.length > 0) {
        context.dispatch(types.MISSION_REQUEST, { ongoing: false, yos: true, gliIds: queryIds })
      }

      context.commit(types.MISSION_SET_GLIDER_FILTER, ids)

      resolve(ids)
    })
  }

  public [types.MISSION_SET_ENSEIGNE_FILTER](context: ActionContext<IMissionState, IRootState>, ids: number[]) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_SET_ENSEIGNE_FILTER, ids)
      if (context.rootGetters.allMissionVisibility) context.dispatch(types.UI_SHOW_ALL_DRAWER_MISSION_MAP, '')
      else context.dispatch(types.UI_HIDE_ALL_DRAWER_MISSION_MAP, '')
      resolve(ids)
    })
  }

  public [types.MISSION_SET_ALARM_FILTER](context: ActionContext<IMissionState, IRootState>, ids: string[]) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_SET_ALARM_FILTER, ids)
      resolve(ids)
    })
  }

  public [types.MISSION_SET_ONGOING_FILTER](context: ActionContext<IMissionState, IRootState>, ongoing: boolean) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_SET_ONGOING_FILTER, ongoing)
      resolve(ongoing)
    })
  }

  public [types.MISSION_SET_SIMULATED_FILTER](context: ActionContext<IMissionState, IRootState>, simulated: boolean) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_SET_SIMULATED_FILTER, simulated)
      resolve(simulated)
    })
  }

  public [types.MISSION_RESET_FILTER](context: ActionContext<IMissionState, IRootState>) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_RESET_FILTER)
      resolve()
    })
  }

  public [types.MISSION_ADD_CONSOLE_ROW](
    context: ActionContext<IMissionState, IRootState>,
    { missionId, row }: { missionId: number; row: ConsoleRow },
  ) {
    return new Promise((resolve, reject) => {
      let msgAdded = false
      //list des consoles a mettre a jour
      const missionsIds = missionId
        ? [missionId]
        : (context.getters.detailMissions as Array<MissionUi>).map(d => d.mission.misId!)

      missionsIds.forEach(id => {
        const uiid = buildMissionUIStateId(id, 'detail')
        const missionState = context.rootGetters.uistatebyuiid(uiid)

        //si la console est ouverte
        if (missionState && missionState.canLogConsoleMsg) {
          context.commit(types.MISSION_ADD_CONSOLE_ROW, { consoleMsgs: missionState.consoleMsgs, row })
          msgAdded = true
        }
      })

      resolve(msgAdded)
    })
  }

  public [types.MISSION_CLEAR_CONSOLE](context: ActionContext<IMissionState, IRootState>, missionId: number) {
    return new Promise((resolve, reject) => {
      const uiid = buildMissionUIStateId(missionId, 'detail')
      const missionState = context.rootGetters.uistatebyuiid(uiid)
      if (missionState) {
        context.commit(types.MISSION_CLEAR_CONSOLE, missionState)
      }
      resolve(missionId)
    })
  }

  public [types.MISSION_ENSURE_MISSION](context: ActionContext<IMissionState, IRootState>, missionId: number) {
    const mission = context.getters.missionsById[missionId]
    if (mission === undefined || !mission._detailLoaded) {
      return context.dispatch(types.MISSION_LOADING_REQUEST, missionId)
    } else {
      return new Promise((resolve, reject) => {
        resolve(context.getters.missionsById[missionId])
      })
    }
  }

  public [types.MISSION_ENSURE_DATA_SCIENCE](context: ActionContext<IMissionState, IRootState>, missionId: number) {
    const mission = context.getters.missionsById[missionId]
    if (mission !== undefined && !mission._dataScienceLoaded) {
      return context.dispatch(types.MISSION_DATA_SCIENCE_REQUEST, { id: missionId, targetYosCycles: null })
    } else {
      return new Promise((resolve, reject) => {
        if (mission === undefined) {
          reject()
        } else {
          resolve(mission.dataScience)
        }
      })
    }
  }

  public [types.MISSION_ENSURE_DATA_NMEA](
    context: ActionContext<IMissionState, IRootState>,
    { typeNmea, id }: { typeNmea: string; id: number },
  ) {
    const mission = context.getters.missionsById[id]
    if (mission !== undefined && !mission._detailNmeaLoaded[typeNmea]) {
      return context.dispatch(types.MISSION_DATA_NMEA_REQUEST, { typeNmea, id })
    } else {
      return new Promise((resolve, reject) => {
        if (mission === undefined) {
          reject()
        } else {
          resolve(mission.dataNmea[typeNmea])
        }
      })
    }
  }

  public [types.MISSION_ENSURE_DRIFT](context: ActionContext<IMissionState, IRootState>, missionId: number) {
    const mission = context.getters.missionsById[missionId]
    if (mission !== undefined && !mission._driftLoaded) {
      return context.dispatch(types.MISSION_DRIFT_REQUEST, missionId)
    } else {
      return new Promise((resolve, reject) => {
        if (mission === undefined) {
          reject()
        } else {
          resolve(mission.yos)
        }
      })
    }
  }

  public [types.MISSION_ENSURE_DATA_NAVIGATION](context: ActionContext<IMissionState, IRootState>, missionId: number) {
    const mission = context.getters.missionsById[missionId]
    if (mission !== undefined && !mission._dataNavigationLoaded) {
      return context.dispatch(types.MISSION_DATA_NAVIGATION_REQUEST, { id: missionId, targetYosCycles: null })
    } else {
      return new Promise((resolve, reject) => {
        if (mission === undefined) {
          reject()
        } else {
          resolve(mission.dataNavigation)
        }
      })
    }
  }

  public [types.MISSION_UPDATE](context: ActionContext<IMissionState, IRootState>, wob: any) {
    return new Promise<void>((resolve, reject) => {
      const mission = context.getters.missionsById[wob.misId] as AloMission
      if (mission !== undefined) {
        if (wob.gliderWaitingMode) {
          //console.log("gliderWaitingMode",wob.gliderWaitingMode)
          //on joue un son a la reconnection d'un glider
          if (
            mission.gliderWaitingMode == GLIDER_MODE.GLIDER_MODE_DISCONNECTED &&
            wob.gliderWaitingMode != GLIDER_MODE.GLIDER_MODE_DISCONNECTED
          ) {
            //On joue le son que si actif
            if (
              !mission.isTestMission &&
              (context.rootGetters.soundNotification == SoundNotification.Enable ||
                (context.rootGetters.soundNotification == SoundNotification.Alert &&
                  wob.gliderWaitingMode == GLIDER_MODE.GLIDER_MODE_WAITING_FOR_ACK) ||
                (context.rootGetters.soundNotification == SoundNotification.DisableSim && !mission.isTestMission))
            ) {
              //https://soundbible.com/mp3/glass_ping-Go445-1207030150.mp3 // pour notif simulation

              var audio = new Audio(
                wob.gliderWaitingMode == GLIDER_MODE.GLIDER_MODE_WAITING_FOR_ACK ? '/alarm.mp3' : '/ding.mp3',
              )
              Vue.nextTick(() => {
                audio.play()
              })
            }
            //on affiche un snack
            context.dispatch(types.UI_ADD_SNACK_MESSAGE, i18n.t('Messages.GliderConnect', { gliKey: mission.gliKey }))
          }
        }
        context.commit(types.MISSION_UPDATE, { mission, wob })
        context.dispatch(types.UI_SELECT_LAST_TIMELINE_MISSION_INDEX, wob.misId)
        resolve()
      } else {
        reject()
      }
    })
  }

  public [types.MISSION_UPDATE_NMEA](context: ActionContext<IMissionState, IRootState>, wob: wobGliderNmea) {
    return new Promise<void>((resolve, reject) => {
      const mission = context.getters.missionsById[wob.misId]
      if (mission !== undefined && mission._detailLoaded) {
        //le detail nmea de la mission est dejà chargé
        if (mission._detailNmeaLoaded[wob.nmeaData.libelle]) {
          context.commit(types.MISSION_UPDATE_NMEA, { mission, wob })
        } else {
          context.dispatch(types.MISSION_DATA_NMEA_REQUEST, { typeNmea: wob.nmeaData.libelle, id: mission.misId })
        }
        resolve()
      } else {
        reject()
      }
    })
  }

  public [types.MISSION_DELETE](context: ActionContext<IMissionState, IRootState>, id: number) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_DELETE, id)
      resolve()
    })
  }

  public [types.MISSION_UPDATE_DOWNLOAD_FILE_STATUS](
    context: ActionContext<IMissionState, IRootState>,
    wob: WobGliderFile,
  ) {
    return new Promise<void>((resolve, reject) => {
      const mission = context.getters.missionsById[wob.targetMisId!] as AloMission
      if (mission !== undefined) {
        if (mission._downloadFileStatusLoaded) {
          context.commit(types.MISSION_UPDATE_DOWNLOAD_FILE_STATUS, { mission, wob })
        }
        //des nouvelles données sont disponible pour la mission
        //on met a jour si besoin les données déja en mémoire

        if (
          wob.gliderFileStep == GliderFileImportStep.CacheGraphFinished ||
          //Ajout de la gestion de l'indexing finished car le CacheGraphFinished n'est plus envoyé
          wob.gliderFileStep == GliderFileImportStep.IndexingFinished
        ) {
          //on vide les cache des images de la mission
          //@ts-ignore
          if (wob.gliderFileType == GliderFileType.gli) delete window[`imgcache_${wob.misId}_nav"}`]
          //@ts-ignore
          else delete window[`imgcache_${wob.misId}_sci"}`]
          //données de navigation
          if (wob.gliderFileType == GliderFileType.gli && mission._dataNavigationLoaded) {
            context
              .dispatch(types.MISSION_DATA_NAVIGATION_REQUEST, {
                id: wob.targetMisId,
                targetYosCycles: wob.targetYosCycles,
              })
              .then(() => {
                context.dispatch(types.UI_REFRESH_NAV_GRAPH_CACHE, wob.targetMisId)
              })
          } else if (
            wob.gliderFileType == GliderFileType.pld1 ||
            wob.gliderFileType == GliderFileType.pld2 ||
            wob.gliderFileType == GliderFileType.ad2cp
          ) {
            //mise à jour des variables sciences de la mission si necessaire
            const mustReloadVariable =
              (wob.importedCsvHeaders?.filter(v => mission.sciPlotVariables.every(mv => mv.variable != v))?.length ??
                0) > 0
            Promise.all(
              mustReloadVariable ? [context.dispatch(types.MISSION_LOADING_SCIVARIABLE_REQUEST, mission.misId)] : [],
            ).then(() => {
              if (mission._dataScienceLoaded) {
                if (wob.gliderFileType == GliderFileType.ad2cp)
                  mission.ad2cpDataIntegrationState = 'Pilot.Ad2cp.DtiUpdateLocalData'
                context
                  .dispatch(types.MISSION_DATA_SCIENCE_REQUEST, {
                    id: wob.targetMisId,
                    targetYosCycles: wob.targetYosCycles,
                  })
                  .then(() => {
                    if (wob.gliderFileType == GliderFileType.ad2cp)
                      mission.ad2cpDataIntegrationState = 'Pilot.Ad2cp.DtiReady'
                    context.dispatch(types.UI_REFRESH_SCI_GRAPH_CACHE, {
                      id: wob.targetMisId,
                      importedCsvHeaders: wob.importedCsvHeaders,
                    })
                  })
              } else {
                //recharge la mission pour avoir les bonne variables
                if (wob.gliderFileType == GliderFileType.ad2cp)
                  mission.ad2cpDataIntegrationState = 'Pilot.Ad2cp.DtiReady'
              }
            })
          }
        } else if (wob.gliderFileType == GliderFileType.ad2cp) {
          if (wob.gliderFileStep == GliderFileImportStep.HttpRunning)
            mission.ad2cpDataIntegrationState = 'Pilot.Ad2cp.DtiStaging'
          if (wob.gliderFileStep == GliderFileImportStep.StagingRunning)
            mission.ad2cpDataIntegrationState = 'Pilot.Ad2cp.DtiStaging'
          if (wob.gliderFileStep == GliderFileImportStep.IndexingRunning)
            mission.ad2cpDataIntegrationState = 'Pilot.Ad2cp.DtiIndexing'

          //plus besoin car ad2cp recoit maintenant le CacheGraphFinished
          /*//Ajout du code de IndexingFinished car pas encore de CacheGraphFinished pour l'ad2cp
          if(wob.gliderFileStep == GliderFileImportStep.IndexingFinished ) {
            mission.ad2cpDataIntegrationState = 'Pilot.Ad2cp.DtiUpdateLocalData'
            context.dispatch(types.MISSION_DATA_SCIENCE_REQUEST,{id:wob.targetMisId,targetYosCycles:wob.targetYosCycles}).then(() => {              
              mission.ad2cpDataIntegrationState = 'Pilot.Ad2cp.DtiReady'
              context.dispatch(types.UI_REFRESH_SCI_GRAPH_CACHE,  {id:wob.targetMisId,importedCsvHeaders:wob.importedCsvHeaders})
            })             
          } */
        }
        resolve()
      } else {
        reject()
      }
    })
  }

  public [types.MISSION_ENSURE_FILETRANSFERT_STATE](
    context: ActionContext<IMissionState, IRootState>,
    missionId: number,
  ) {
    return new Promise<void>((resolve, reject) => {
      const mission = context.getters.missionsById[missionId]
      if (mission === undefined) {
        reject()
      } else {
        const fullKey = buildDataMissionKey(missionId, DataPoolMissionKeys['fileTransfer.config'])
        context.dispatch(types.DATAPOOL_ENSURE, { usrId: null, fullKey }).then(() => {
          resolve()
        })
      }
    })
  }

  public [types.MISSION_ENSURE_DOWNLOAD_FILE_STATUS](
    context: ActionContext<IMissionState, IRootState>,
    missionId: number,
  ) {
    const mission = context.getters.missionsById[missionId]
    if (mission !== undefined && !mission._downloadFileStatusLoaded) {
      return context.dispatch(types.MISSION_DOWNLOAD_FILE_STATUS_REQUEST, missionId)
    } else {
      return new Promise((resolve, reject) => {
        if (mission === undefined) {
          reject()
        } else {
          resolve(mission.downloadFileStatus)
        }
      })
    }
  }

  public [types.MISSION_DOWNLOAD_FILE_STATUS_REQUEST](context: ActionContext<IMissionState, IRootState>, id: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_DOWNLOAD_FILE_STATUS_REQUEST, id)
      apiMission
        .getGliderFile(id)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const datas = (resp.data.data as AloBaseArray<AloGliderFileShort>).items
            //context.commit les données de la mission
            context.commit(types.MISSION_DOWNLOAD_FILE_STATUS_SUCCESS, { id, datas })
            resolve(datas)
          } else {
            context.commit(types.MISSION_DOWNLOAD_FILE_STATUS_ERROR, id)
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_DOWNLOAD_FILE_STATUS_ERROR, id)
          reject()
        })
    })
  }

  public [types.MISSION_END](context: ActionContext<IMissionState, IRootState>, id: number) {
    return new Promise((resolve, reject) => {})
  }

  public [types.MISSION_LOGBOOK_REQUEST](context: ActionContext<IMissionState, IRootState>, id: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_LOGBOOK_REQUEST, id)
      apiMission
        .getLogbook(id)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const datas = (resp.data.data as AloLogbookChannel).messageArray
            //context.commit les données de la mission
            context.commit(types.MISSION_LOGBOOK_SUCCESS, { id, datas })
            resolve(datas)
          } else {
            context.commit(types.MISSION_LOGBOOK_ERROR, id)
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_LOGBOOK_ERROR, id)
          reject()
        })
    })
  }

  public [types.MISSION_LOGBOOK_ITEM_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { misId, logBookId }: { misId: number; logBookId: number },
  ) {
    return new Promise((resolve, reject) => {
      //on ne lance la requete d'un item que si le logBook est chargé
      const mission = context.getters.missionsById[misId]
      if (mission !== undefined && mission._logBookLoaded) {
        context.commit(types.MISSION_LOGBOOK_ITEM_REQUEST, { misId, logBookId })
        apiMission
          .getLogbookItem(logBookId)
          .then(resp => {
            if (resp.data.resultCode == ApiResultCode.Ok) {
              const data = resp.data.data as AloLogbookItem
              //context.commit les données de la mission
              context.commit(types.MISSION_LOGBOOK_ITEM_SUCCESS, { misId, data })
              resolve(data)
            } else {
              context.commit(types.MISSION_LOGBOOK_ITEM_ERROR, { misId, logBookId })
              reject()
            }
          })
          .catch(error => {
            context.commit(types.MISSION_LOGBOOK_ITEM_ERROR, { misId, logBookId })
            reject()
          })
      } else {
        resolve(null)
      }
    })
  }

  public [types.MISSION_LOGBOOK_SEND_MESSAGE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { misId, message }: { misId: number; message: string },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_LOGBOOK_SEND_MESSAGE_REQUEST, misId)
      apiMission
        .addMessageToLogbook({ misId, message })
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const data = resp.data.data as AloLogbookItem
            //context.commit les données de la mission
            context.commit(types.MISSION_LOGBOOK_ITEM_SUCCESS, { misId, data })
            resolve(data)
          } else {
            context.commit(types.MISSION_LOGBOOK_SEND_MESSAGE_ERROR, misId)
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_LOGBOOK_SEND_MESSAGE_ERROR, misId)
          reject()
        })
    })
  }

  public [types.MISSION_LOGBOOK_UPDATE_MESSAGE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { misId, lbkId, message }: { misId: number; lbkId: number; message: string },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_LOGBOOK_UPDATE_MESSAGE_REQUEST, lbkId)
      apiMission
        .updateMessageToLogbook({ lbkId, message })
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const data = resp.data.data as AloLogbookItem
            //context.commit les données de la mission
            context.commit(types.MISSION_LOGBOOK_ITEM_SUCCESS, { misId, data })
            resolve(data)
          } else {
            context.commit(types.MISSION_LOGBOOK_UPDATE_MESSAGE_ERROR, { misId, lbkId })
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_LOGBOOK_UPDATE_MESSAGE_ERROR, { misId, lbkId })
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_SEND_MAIL_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_SEND_MAIL_REQUEST)
      apiMission
        .getSendMailConfig(itemId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModSendMail
            context.commit(types.MISSION_IRIS_SEND_MAIL_SUCCESS, { id: itemId, sendMailConfig: irisConfig.data })
            patchRuntimeModuleEnable(context, 'SendMail', irisConfig.data, context.getters.missionsById[itemId]?.gliId)
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_SEND_MAIL_ERROR)
            const error = resp.data.data as AloError
            console.error(error.errorMessage)
            reject(error)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_SEND_MAIL_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_UPDATE_MODIFIED_SEND_MAIL](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, newProperties }: { itemId: number; newProperties: Object },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_UPDATE_MODIFIED_SEND_MAIL, { itemId, newProperties })
      resolve(context.state.modifiedSendMailConfigs[itemId])
    })
  }

  public [types.MISSION_CANCEL_MODIFIED_SEND_MAIL](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_CANCEL_MODIFIED_SEND_MAIL, itemId)
      resolve(context.state.modifiedSendMailConfigs[itemId])
    })
  }

  public [types.MISSION_IRIS_SEND_MAIL_SAVE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    itemId: number,
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_SEND_MAIL_SAVE_REQUEST)
      apiMission
        .saveSendMailConfig(itemId, context.state.modifiedSendMailConfigs[itemId])
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModSendMail
            context.commit(types.MISSION_IRIS_SEND_MAIL_SAVE_SUCCESS, { id: itemId, sendMailConfig: irisConfig.data })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_SEND_MAIL_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_SEND_MAIL_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_SEND_MAIL_ENABLE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, enable }: { itemId: number; enable: boolean },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_SEND_MAIL_SAVE_REQUEST)
      apiMission
        .enableSendMailModule(itemId, enable)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModSendMail
            context.commit(types.MISSION_IRIS_SEND_MAIL_SAVE_SUCCESS, { id: itemId, sendMailConfig: irisConfig.data })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_SEND_MAIL_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_SEND_MAIL_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_ENSURE_MISSION_PLAN](context: ActionContext<IMissionState, IRootState>, missionId: number) {
    const mission = context.getters.missionsById[missionId]
    if (mission !== undefined && !mission._missionPlanLoaded) {
      return context.dispatch(types.MISSION_PLAN_REQUEST, missionId)
    } else {
      return new Promise((resolve, reject) => {
        if (mission === undefined) {
          reject()
        } else {
          resolve(mission.missionPlanStaging)
        }
      })
    }
  }

  public [types.MISSION_PLAN_REQUEST](context: ActionContext<IMissionState, IRootState>, missionId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_PLAN_REQUEST, missionId)
      apiMission
        .getLastMissionPlan(missionId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const missionPlan = resp.data.data as AloMissionPlan
            //s'assure que le contexte sera existant
            context.dispatch(types.UI_ENSURE_MISSIONPLAN_STATE, { id: missionId, typeUiid: 'mission' }).then(rep => {
              context.commit(types.MISSION_PLAN_SUCCESS, { misId: missionId, missionPlan })
              resolve(missionPlan)
            })
          } else {
            context.commit(types.MISSION_PLAN_ERROR, missionId)
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_PLAN_ERROR, missionId)
          reject()
        })
    })
  }

  public [types.MISSION_PLAN_PUBLISH_REQUEST](context: ActionContext<IMissionState, IRootState>, mpsId: number) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_PLAN_PUBLISH_REQUEST, mpsId)
      apiMission
        .publishMissionPlan(mpsId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            context.commit(types.MISSION_PLAN_PUBLISH_SUCCESS, mpsId)
            resolve()
          } else {
            context.commit(types.MISSION_PLAN_PUBLISH_ERROR, mpsId)
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_PLAN_PUBLISH_ERROR, mpsId)
          reject()
        })
    })
  }

  public [types.MISSION_PLAN_SAVE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    missionPlan: AloMissionPlan,
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_PLAN_SAVE_REQUEST, missionPlan)
      apiMission
        .saveLastMissionPlan(missionPlan)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const missionPlan = resp.data.data as AloMissionPlan
            //context.commit les données de la mission
            context.commit(types.MISSION_PLAN_SAVE_SUCCESS, missionPlan)
            resolve(missionPlan)
          } else {
            context.commit(types.MISSION_PLAN_SAVE_ERROR, missionPlan)
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_PLAN_SAVE_ERROR, missionPlan)
          reject()
        })
    })
  }

  public [types.MISSION_PLAN_POPULATE_FROM_TEMPLATE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { misId, mptId }: { misId: number; mptId: number },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_PLAN_POPULATE_FROM_TEMPLATE_REQUEST, { misId, mptId })
      apiMission
        .populateMissionPlanFromTemplate(mptId, misId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const missionPlan = resp.data.data as AloMissionPlan
            //context.commit les données de la mission
            context.commit(types.MISSION_PLAN_POPULATE_FROM_TEMPLATE_SUCCESS, missionPlan)
            resolve(missionPlan)
          } else {
            context.commit(types.MISSION_PLAN_POPULATE_FROM_TEMPLATE_ERROR, { misId, mptId })
            reject()
          }
        })
        .catch(error => {
          context.commit(types.MISSION_PLAN_POPULATE_FROM_TEMPLATE_ERROR, { misId, mptId })
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_MISSION_PLAN_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_MISSION_PLAN_REQUEST)
      context.dispatch(types.UI_ENSURE_MISSIONPLAN_STATE, { id: itemId, typeUiid: 'mission' }).then(rep => {
        apiMission
          .getIrisMissionPlanInfos(itemId)
          .then(resp => {
            if (resp.data.resultCode == ApiResultCode.Ok) {
              //lance la creation des models de mission state Ui si necessaire
              const irisConfig = resp.data.data as IrisrawModMissionPlan

              const uiid = buildMissionPlanStateId(itemId, 'mission')
              const missionPlantState = context.rootGetters.uistatebyuiid(uiid) as MissionPlanUiState
              if (deepEqual(context.state.originalIrisMissionPlan[itemId], missionPlantState.missionPlan)) {
                const plan = MissionPlanUiState.parseMissionPlan(JSON.stringify(irisConfig.data))
                context.commit(types.UI_MISSIONPLAN_SET_MISSIONPLAN, { uiid, plan })
              }
              context.commit(types.MISSION_IRIS_MISSION_PLAN_SUCCESS, { id: itemId, missionPlanState: irisConfig.data })
              resolve(irisConfig.data)
            } else {
              context.commit(types.MISSION_IRIS_MISSION_PLAN_ERROR)
              const error = resp.data.data as AloError
              console.error(error.errorMessage)
              reject(error)
            }
          })
          .catch(resp => {
            context.commit(types.MISSION_IRIS_MISSION_PLAN_ERROR)
            reject()
          })
      })
    })
  }

  public [types.MISSION_IRIS_MISSION_PLAN_CONFIG_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    itemId: number,
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_REQUEST)
      apiMission
        .getIrisMissionPlanConfig(itemId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModMissionPlanConfig
            context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_SUCCESS, {
              id: itemId,
              missionPlanConfig: irisConfig.data,
            })
            patchRuntimeModuleEnable(
              context,
              'MissionPlan',
              irisConfig.data,
              context.getters.missionsById[itemId]?.gliId,
            )
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_ERROR)
            const error = resp.data.data as AloError
            console.error(error.errorMessage)
            reject(error)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_UPDATE_MODIFIED_MISSION_PLAN_CONFIG](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, newProperties }: { itemId: number; newProperties: Object },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_UPDATE_MODIFIED_MISSION_PLAN_CONFIG, { itemId, newProperties })
      resolve(context.state.modifiedMissionPlanConfigs[itemId])
    })
  }

  public [types.MISSION_CANCEL_MODIFIED_MISSION_PLAN_CONFIG](
    context: ActionContext<IMissionState, IRootState>,
    itemId: number,
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_CANCEL_MODIFIED_MISSION_PLAN_CONFIG, itemId)
      resolve(context.state.modifiedMissionPlanConfigs[itemId])
    })
  }

  public [types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    itemId: number,
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_REQUEST)
      apiMission
        .saveIrisMissionPlanConfig(itemId, context.state.modifiedMissionPlanConfigs[itemId])
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModMissionPlanConfig
            context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_SUCCESS, {
              id: itemId,
              missionPlanConfig: irisConfig.data,
            })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_MISSION_PLAN_CONFIG_ENABLE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, enable }: { itemId: number; enable: boolean },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_REQUEST)
      apiMission
        .enableIrisMissionPlanModule(itemId, enable)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModMissionPlanConfig
            context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_SUCCESS, {
              id: itemId,
              missionPlanConfig: irisConfig.data,
            })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_MISSION_PLAN_CONFIG_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_AD2CP_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_AD2CP_REQUEST)
      apiMission
        .getAd2cpConfig(itemId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModAd2cp
            context.commit(types.MISSION_IRIS_AD2CP_SUCCESS, { id: itemId, ad2cpConfig: irisConfig.data })
            patchRuntimeModuleEnable(context, 'AD2CP', irisConfig.data, context.getters.missionsById[itemId]?.gliId)
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_AD2CP_ERROR)
            const error = resp.data.data as AloError
            console.error(error.errorMessage)
            reject(error)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_AD2CP_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_UPDATE_MODIFIED_AD2CP](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, newProperties }: { itemId: number; newProperties: Object },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_UPDATE_MODIFIED_AD2CP, { itemId, newProperties })
      resolve(context.state.modifiedAd2cpConfigs[itemId])
    })
  }

  public [types.MISSION_CANCEL_MODIFIED_AD2CP](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_CANCEL_MODIFIED_AD2CP, itemId)
      resolve(context.state.modifiedAd2cpConfigs[itemId])
    })
  }

  public [types.MISSION_IRIS_AD2CP_SAVE_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_AD2CP_SAVE_REQUEST)
      apiMission
        .saveAd2cpConfig(itemId, context.state.modifiedAd2cpConfigs[itemId])
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModAd2cp
            context.commit(types.MISSION_IRIS_AD2CP_SAVE_SUCCESS, { id: itemId, ad2cpConfig: irisConfig.data })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_AD2CP_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_AD2CP_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_AD2CP_ENABLE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, enable }: { itemId: number; enable: boolean },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_AD2CP_SAVE_REQUEST)
      apiMission
        .enableAd2cpModule(itemId, enable)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModAd2cp
            context.commit(types.MISSION_IRIS_AD2CP_SAVE_SUCCESS, { id: itemId, ad2cpConfig: irisConfig.data })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_AD2CP_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_AD2CP_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_AD2CP_RUN_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_IRIS_AD2CP_RUN_REQUEST)
      apiMission
        .runAd2cpConfig(itemId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            context.commit(types.MISSION_IRIS_AD2CP_RUN_SUCCESS)
            resolve()
          } else {
            context.commit(types.MISSION_IRIS_AD2CP_RUN_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_AD2CP_RUN_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_CORIOLIS_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_CORIOLIS_REQUEST)
      apiMission
        .getCoriolisConfig(itemId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModCoriolis
            context.commit(types.MISSION_IRIS_CORIOLIS_SUCCESS, { id: itemId, coriolisConfig: irisConfig.data })
            patchRuntimeModuleEnable(context, 'Coriolis', irisConfig.data, context.getters.missionsById[itemId]?.gliId)
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_CORIOLIS_ERROR)
            const error = resp.data.data as AloError
            console.error(error.errorMessage)
            reject(error)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_CORIOLIS_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_UPDATE_MODIFIED_CORIOLIS](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, newProperties }: { itemId: number; newProperties: Object },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_UPDATE_MODIFIED_CORIOLIS, { itemId, newProperties })
      resolve(context.state.modifiedCoriolisConfigs[itemId])
    })
  }

  public [types.MISSION_CANCEL_MODIFIED_CORIOLIS](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_CANCEL_MODIFIED_CORIOLIS, itemId)
      resolve(context.state.modifiedCoriolisConfigs[itemId])
    })
  }

  public [types.MISSION_IRIS_CORIOLIS_SAVE_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_CORIOLIS_SAVE_REQUEST)
      apiMission
        .saveCoriolisConfig(itemId, context.state.modifiedCoriolisConfigs[itemId])
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModCoriolis
            context.commit(types.MISSION_IRIS_CORIOLIS_SAVE_SUCCESS, { id: itemId, coriolisConfig: irisConfig.data })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_CORIOLIS_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_CORIOLIS_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_CORIOLIS_ENABLE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, enable }: { itemId: number; enable: boolean },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_CORIOLIS_SAVE_REQUEST)
      apiMission
        .enableCoriolisModule(itemId, enable)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            const irisConfig = resp.data.data as IrisrawModCoriolis
            context.commit(types.MISSION_IRIS_CORIOLIS_SAVE_SUCCESS, { id: itemId, coriolisConfig: irisConfig.data })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_CORIOLIS_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_CORIOLIS_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_ENDMISSION_UPDATE_PROGRESSION](
    context: ActionContext<IMissionState, IRootState>,
    wob: WobSimProgress,
  ) {
    return new Promise((resolve, reject) => {
      const mission = context.getters.missionsById[wob.misId]
      //console.log("mission",mission)
      if (mission) {
        context.commit(types.MISSION_ENDMISSION_UPDATE_PROGRESSION, { mission, wob })
      }
      resolve(wob)
    })
  }

  public [types.MISSION_IRIS_CORIOLIS_RUN_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_IRIS_CORIOLIS_RUN_REQUEST)
      apiMission
        .runCoriolisConfig(itemId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //lance la creation des models de mission state Ui si necessaire
            context.commit(types.MISSION_IRIS_CORIOLIS_RUN_SUCCESS)
            resolve()
          } else {
            context.commit(types.MISSION_IRIS_CORIOLIS_RUN_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_CORIOLIS_RUN_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_AIS_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_AIS_REQUEST)
      apiMission
        .getAisConfig(itemId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const irisConfig = resp.data.data as IrisrawModAis
            context.commit(types.MISSION_IRIS_AIS_SUCCESS, { id: itemId, aisConfig: irisConfig.data })
            patchRuntimeModuleEnable(context, 'AIS', irisConfig.data, context.getters.missionsById[itemId]?.gliId)
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_AIS_ERROR)
            const error = resp.data.data as AloError
            console.error(error.errorMessage)
            reject(error)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_AIS_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_UPDATE_MODIFIED_AIS](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, newProperties }: { itemId: number; newProperties: Object },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_UPDATE_MODIFIED_AIS, { itemId, newProperties })
      resolve(context.state.modifiedAisConfigs[itemId])
    })
  }

  public [types.MISSION_CANCEL_MODIFIED_AIS](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_CANCEL_MODIFIED_AIS, itemId)
      resolve(context.state.modifiedAisConfigs[itemId])
    })
  }

  public [types.MISSION_IRIS_AIS_SAVE_REQUEST](context: ActionContext<IMissionState, IRootState>, itemId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_AIS_SAVE_REQUEST)
      apiMission
        .saveAisConfig(itemId, context.state.modifiedAisConfigs[itemId])
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const irisConfig = resp.data.data as IrisrawModAis
            context.commit(types.MISSION_IRIS_AIS_SAVE_SUCCESS, { id: itemId, aisConfig: irisConfig.data })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_AIS_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_AIS_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_AIS_ENABLE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, enable }: { itemId: number; enable: boolean },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_AIS_SAVE_REQUEST)
      apiMission
        .enableAisModule(itemId, enable)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const irisConfig = resp.data.data as IrisrawModAis
            context.commit(types.MISSION_IRIS_AIS_SAVE_SUCCESS, { id: itemId, aisConfig: irisConfig.data })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_AIS_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_IRIS_AIS_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_AIS_STATUS_UPDATE](
    context: ActionContext<IMissionState, IRootState>,
    { id, module_status }: { id: number; module_status: string },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_AIS_STATUS_UPDATE, { id, module_status })
      resolve(null)
    })
  }

  /**
   * Action wrap pour commit GLIDER_RUNTIME_PATCH_MODULE_ENABLE. Permet le
   * dispatch depuis n'importe quel module (cross-module via `context.dispatch`).
   * Cf src/store/modules/glider/actions.ts (GLIDER_MODCONF_UPDATE) qui consomme.
   */
  public [types.GLIDER_RUNTIME_PATCH_MODULE_ENABLE](
    context: ActionContext<IMissionState, IRootState>,
    { gliId, moduleKey, enable }: { gliId: number; moduleKey: GliderModuleApiId; enable: boolean },
  ) {
    return new Promise(resolve => {
      context.commit(types.GLIDER_RUNTIME_PATCH_MODULE_ENABLE, { gliId, moduleKey, enable })
      resolve(null)
    })
  }

  /**
   * Action wrap pour commit GLIDER_RUNTIME_PATCH_ALARM. Consommée par le handler
   * WobGliderAlarm (cf src/store/modules/socket/actions.ts).
   */
  public [types.GLIDER_RUNTIME_PATCH_ALARM](
    context: ActionContext<IMissionState, IRootState>,
    { gliId, alarmCode }: { gliId: number; alarmCode: number },
  ) {
    return new Promise(resolve => {
      context.commit(types.GLIDER_RUNTIME_PATCH_ALARM, { gliId, alarmCode })
      resolve(null)
    })
  }

  public [types.MISSION_RUN_END_SIMULATION_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { misId, data }: { misId: number; data: ISmartSimulation },
  ) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.MISSION_RUN_END_SIMULATION_REQUEST, misId)
      Vue.set(context.rootState.missionVirtual.vimLaunchDic, misId, true)
      apiMission
        .runEndMissionSimulation(misId, data)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            //console.log(resp.data.data)
            const smartMission = resp.data.data as ISmartMission
            context.commit(types.MISSION_RUN_END_SIMULATION_SUCCESS, { misId, smartMission })
            //Recup les mission virtuelles
            // context.dispatch(types.MISSIONVIRTUAL_REQUEST).then(resultMisVirtual=> {
            //   context.dispatch(types.MISSIONGLOBAL_SELECT_DETAIL_REQUEST,context.getters.originalAdmMissionGlobal.MIG_ID).then((result:AloDbMissionGlobal) =>{
            //     context.dispatch(types.MISSIONGLOBAL_OPEN_DETAIL,result.MIG_ID).then(()=>{
            //       context.dispatch(types.MISSIONGLOBAL_OPEN_LAST_SIMULATION,result.MIG_ID).then(()=>{
            //         resolve(result)
            //       })
            //     })
            //   })
            // })

            //Recup le detail de la mission Global
            // context.dispatch(types.MISSIONGLOBAL_SELECT_DETAIL_REQUEST,context.getters.originalAdmMissionGlobal.MIG_ID).then((result:AloDbMissionGlobal) =>{
            //   const maxId = result.misIdArray.reduce((max,id)=> id>max?id:max ,0)
            //   context.dispatch(types.MISSIONVIRTUAL_SELECT_DETAIL_REQUEST,maxId).then(resultMisVirtual=>{
            //     resolve(result)
            //   })
            // })
            resolve()
          } else {
            console.error('ATTENTION une reponse Code 200 contient une erreur')
            context.commit(types.MISSION_RUN_END_SIMULATION_ERROR)
            const error = resp.data.data as AloError
            context.dispatch('UI_ADD_SNACK_MESSAGE', i18n.t('Admin.ErrorSimulation', { error: error.errorMessage }))
            console.error(error)
            reject(error)
          }
        })
        .catch(error => {
          context.commit(types.MISSION_RUN_END_SIMULATION_ERROR)
          if (error.response) {
            const er = error.response.data.data as AloError
            context.dispatch('UI_ADD_SNACK_MESSAGE', i18n.t('Admin.ErrorSimulation', { error: er.errorMessage }))
            console.error(er)
            reject(er)
          } else {
            context.dispatch('UI_ADD_SNACK_MESSAGE', i18n.t('Admin.ErrorOnSimulation'))
            reject()
          }
        })
    })
  }
  public [types.MISSION_IRIS_ENCRYPTION_GET_REQUEST](context: ActionContext<IMissionState, IRootState>, misId: number) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_ENCRYPTION_GET_REQUEST)
      apiMission
        .getEncryptionConfig(misId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            context.commit(types.MISSION_IRIS_ENCRYPTION_GET_SUCCESS)
            resolve(resp.data.data)
          } else {
            context.commit(types.MISSION_IRIS_ENCRYPTION_GET_ERROR)
            const error = resp.data.data as AloError
            reject(error)
          }
        })
        .catch(() => {
          context.commit(types.MISSION_IRIS_ENCRYPTION_GET_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_ENCRYPTION_SAVE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { misId, passphrase }: { misId: number; passphrase: string },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_ENCRYPTION_SAVE_REQUEST)
      apiMission
        .saveEncryptionConfig(misId, passphrase)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            context.commit(types.MISSION_IRIS_ENCRYPTION_SAVE_SUCCESS)
            context.dispatch('UI_ADD_SNACK_MESSAGE', i18n.t('Pilot.Encryption.SaveSuccess'))
            resolve(null)
          } else {
            context.commit(types.MISSION_IRIS_ENCRYPTION_SAVE_ERROR)
            context.dispatch('UI_ADD_SNACK_MESSAGE', i18n.t('Pilot.Encryption.SaveError'))
            reject(resp.data.data as AloError)
          }
        })
        .catch(() => {
          context.commit(types.MISSION_IRIS_ENCRYPTION_SAVE_ERROR)
          context.dispatch('UI_ADD_SNACK_MESSAGE', i18n.t('Pilot.Encryption.SaveError'))
          reject()
        })
    })
  }

  public [types.MISSION_LBK_ALERT_SAVE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { misId, alertStatus }: { misId: number; alertStatus: number },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_LBK_ALERT_SAVE_REQUEST)
      apiMission
        .saveLkb(misId, alertStatus)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const mission = resp.data.data as AloMission
            context.commit(types.MISSION_LBK_ALERT_SAVE_SUCCESS, misId)
            resolve(mission)
          } else {
            context.commit(types.MISSION_LBK_ALERT_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(resp => {
          context.commit(types.MISSION_LBK_ALERT_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_QUICKCONTROL_CONFIG_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    itemId: number,
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_QUICKCONTROL_CONFIG_REQUEST)
      apiMission
        .getQuickControlConfig(itemId)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const irisConfig = resp.data.data as IrisrawModQuickControlConfig
            context.commit(types.MISSION_IRIS_QUICKCONTROL_CONFIG_SUCCESS, {
              id: itemId,
              config: irisConfig.data,
            })
            patchRuntimeModuleEnable(
              context,
              'QuickControl',
              irisConfig.data,
              context.getters.missionsById[itemId]?.gliId,
            )
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_QUICKCONTROL_CONFIG_ERROR)
            const error = resp.data.data as AloError
            console.error(error.errorMessage)
            reject(error)
          }
        })
        .catch(() => {
          context.commit(types.MISSION_IRIS_QUICKCONTROL_CONFIG_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_REQUEST](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, config }: { itemId: number; config: IrisConfigContainer<IQuickControlConfig> },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_REQUEST)
      apiMission
        .putQuickControlConfig(itemId, config)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const irisConfig = resp.data.data as IrisrawModQuickControlConfig
            context.commit(types.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_SUCCESS, {
              id: itemId,
              config: irisConfig.data,
            })
            resolve(irisConfig.data)
          } else {
            context.commit(types.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_ERROR)
            reject(resp.data.data as AloError)
          }
        })
        .catch(() => {
          context.commit(types.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_ERROR)
          reject()
        })
    })
  }

  public [types.MISSION_UPDATE_MODIFIED_QUICKCONTROL_CONFIG](
    context: ActionContext<IMissionState, IRootState>,
    { itemId, config }: { itemId: number; config: IrisConfigContainer<IQuickControlConfig> },
  ) {
    return new Promise(resolve => {
      context.commit(types.MISSION_UPDATE_MODIFIED_QUICKCONTROL_CONFIG, { itemId, config })
      resolve(context.state.modifiedQuickControlConfigs[itemId])
    })
  }

  public [types.MISSION_CANCEL_MODIFIED_QUICKCONTROL](
    context: ActionContext<IMissionState, IRootState>,
    itemId: number,
  ) {
    return new Promise(resolve => {
      context.commit(types.MISSION_CANCEL_MODIFIED_QUICKCONTROL, itemId)
      resolve(context.state.modifiedQuickControlConfigs[itemId])
    })
  }
}

export default util.classToObject(MissionAction)
